// IdTag — the house way to show an id. A raw id (esp. a uuid) the operator can't
// act on is noise; this makes every id USEFUL: the scannable short form is shown,
// the full id is on hover (title), and a copy button appears on hover to grab the
// full value. For an id that links to another entity, use a real nav link instead
// (this is for the entity's own reference id).
//
// WHY `copyable` EXISTS. The copy affordance is a real <button>, and a <button> may
// not contain another <button> — so an IdTag placed inside a clickable row or card
// produces invalid HTML and a validateDOMNesting warning on every render. Of the 126
// IdTags in the console, 11 sat inside one. Those pass `copyable={false}`: the id
// still shows, and the full value is still on hover, but the nested control is gone
// and the surrounding row keeps its own single, unambiguous click target.
//
// When the id IS the point of its cell and copying matters, put <CopyIdButton /> next
// to the clickable element instead of inside it — see reports/tabs/RunsTab.jsx.
import React from 'react';
import { Copy } from 'lucide-react';
import { shortId } from '../lib/format';
import { toast , copyToClipboard } from '../lib/buses';

/** The copy control on its own, for placing BESIDE a clickable element rather than in it. */
export const CopyIdButton = ({ value, className = '' }) => {
  if (value == null || value === '') return null;
  const full = String(value);
  return (
    <button
      type="button"
      // The row this sits next to is usually clickable too; without this the copy also
      // triggers the row's handler and the operator gets a navigation they didn't ask for.
      onClick={(e) => { e.stopPropagation(); copyToClipboard(full, { label: 'ID copied', detail: full }); }}
      title="Copy full ID"
      className={`text-[#C5C2BC] hover:text-[#1F2937] transition-colors focus:opacity-100 ${className}`}
    >
      <Copy className="w-2.5 h-2.5" />
    </button>
  );
};

/**
 * A VALUE SHOWN IN FULL, WITH A COPY. IdTag's sibling for things that are not ids.
 *
 * IdTag shortens — right for a uuid, wrong for a slug, a URL or a reference, where the whole
 * string IS the useful thing and truncating it defeats the point. Same affordance otherwise:
 * the copy button appears on hover and goes through `copyToClipboard`, so a browser that blocks
 * the clipboard says so instead of claiming success (see lib/buses).
 *
 * `copyValue` exists because what you SHOW and what you PASTE are not always the same string —
 * a public link is displayed without its scheme to keep the row short, but pasting
 * "tkt.ug/domain/x" into a message gives you dead text, so the full https:// form is copied.
 */
export const CopyValue = ({ value, copyValue, label = 'Copied', title, className = '', dash = '—' }) => {
  if (value == null || value === '') return <span className="text-[#9CA3AF]">{dash}</span>;
  const shown = String(value);
  const full = String(copyValue ?? value);
  return (
    <span className={`inline-flex items-center gap-1.5 group/cv ${className}`} title={title || full}>
      <span className="break-all">{shown}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); copyToClipboard(full, { label, detail: full }); }}
        title={`Copy ${full}`}
        className="text-[#C5C2BC] hover:text-[#1F2937] opacity-0 group-hover/cv:opacity-100 transition-opacity focus:opacity-100 flex-shrink-0"
      >
        <Copy className="w-3 h-3" />
      </button>
    </span>
  );
};

export const IdTag = ({ value, className = '', dash = '—', format, copyable = true }) => {
  if (value == null || value === '') return <span className="text-[#9CA3AF]">{dash}</span>;
  const full = String(value);
  // `format` only changes what's SHOWN (e.g. order codes dashed every 4); the raw
  // value is still what's copied and shown on hover, so search/paste stay correct.
  const shown = format ? format(full) : shortId(full);
  const copy = (e) => {
    e.stopPropagation();
    copyToClipboard(full, { label: 'ID copied', detail: full });
  };
  return (
    <span className={`text-nowrap inline-flex items-center gap-1 font-mono group/id ${className}`} title={full}>
      {shown}
      {copyable && (
        <button type="button" onClick={copy} title="Copy full ID" className="text-[#C5C2BC] hover:text-[#1F2937] opacity-0 group-hover/id:opacity-100 transition-opacity focus:opacity-100"><Copy className="w-2.5 h-2.5" /></button>
      )}
    </span>
  );
};
